import {
  ActionActor,
  FileResourceType,
  IAutomationActionTemplateCreateDocumentFromTemplate,
  IAutomationTemplate,
  IExperienceTemplateResource,
  IExperienceTemplateResourceGoogleDoc,
  IExperienceTemplateResourceGoogleDriveFolder,
  IExperienceTemplateResourceMicrosoftOneDriveFolder,
  IExperienceTemplateResourceMicrosoftSharepointFolder,
  ITemplateResource,
  StorageContext,
  SubmitTargetType,
  TemplateResourceType
} from '@dadohr/shared/domainTypes/experience';
import { CustomUserField } from '@dadohr/shared/domainTypes/org';
import { CF } from '@dadohr/shared/versions';
import {
  Button,
  Chip,
  FormHelperText,
  Link,
  TextField,
  Typography
} from '@material-ui/core';
import { compact, mapValues } from 'lodash';
import { CheckCircle, ExternalLink, PlusCircle } from 'lucide-react';
import { SetStateAction, useEffect, useMemo, useState } from 'react';
import shortid from 'shortid';
import { AutocompleteMultiFreeSolo } from '../../../../../../../components/Autocomplete';
import { ButtonWithPromise } from '../../../../../../../components/ButtonWithPromise';
import { DataFieldWithLabel } from '../../../../../../../components/DataFieldWithLabel';
import { FormRow } from '../../../../../../../components/Form/FormRow';
import { InfoBox } from '../../../../../../../components/InfoBox';
import { InlineLoader } from '../../../../../../../components/Loader';
import {
  TokenDefinition,
  TokenSyntaxHelpDialog
} from '../../../../../../../components/MarkdownText';
import { Resource } from '../../../../../../../components/Resource';
import { TextFieldWithReplacementTokens } from '../../../../../../../components/TextFieldWithReplacementTokens';
import { useDialogState } from '../../../../../../../hooks/useDialogState';
import { useUnsafeTranslation } from '../../../../../../../i18next';
import { FlexContainerVertical } from '../../../../../../../layout/Flex';
import { useCurrentUser } from '../../../../../../../services/currentUser';
import { callFirebaseFunction } from '../../../../../../../services/firebaseFunctions';
import { toGoogleDocLink } from '../../../../../../../services/google';
import {
  interpolate,
  userToInterpolation
} from '../../../../../../../services/set/interpolation';
import { now } from '../../../../../../../services/time';
import { isValidUrl } from '../../../../../../../services/url';
import {
  EXAMPLE_USER_MAX,
  getUsersByOrgId
} from '../../../../../../../services/user';
import { OnCompleteHooks } from '../../../TaskBuilder/components/BlockSettings/SubTask/onCompleteHooks';
import {
  getGoogleDriveFolderResource,
  SendToSelector,
  SharepointSiteAndDriveSelector
} from '../../../TaskBuilder/components/BlockSettings/SubTask/Submit';
import {
  BlockSettingsSubPage,
  TaskBuilderContext
} from '../../../TaskBuilder/types';
import { ActionActorSelectorMulti } from '../../shared';
import {
  AutomationBuilderActionValidationState,
  AutomationBuilderActionValidationStateItem,
  useFieldValidator,
  VALIDATORS_CREATE_DOCUMENT_FROM_TEMPLATE
} from '../../validation';

export type CheckDocumentResponseMS =
  | {
      status: 'Error';
      error: string;
    }
  | {
      status: 'Ok';
      driveItemData: { driveId?: string; driveItemId: string; url: string };
      variables: string[]; // we might want to warn the user if there are none
    };

export type CheckDocumentResponseGoogle =
  | {
      status: 'Error';
      error: string;
    }
  | {
      status: 'Ok';
      documentId: string;
      variables: string[]; // we might want to warn the user if there are none
    };

export enum CreateDocumentProviderType {
  GOOGLE = 'GOOGLE',
  MICROSOFT = 'MICROSOFT'
}

const getUniqueTemplateResource = <
  T extends ITemplateResource<TemplateResourceType>
>(
  res: IExperienceTemplateResource[],
  type: TemplateResourceType
) =>
  (res.find((r) => r.type === type) as T & IExperienceTemplateResource) || null;

export const ActionSettingsCreateDocumentFromTemplate = ({
  value,
  onChange,
  expTpl,
  aTpl,
  validation,
  setValidation,
  resources,
  onChangeResources,
  tokens,
  providerType,
  customFields,
  goToSubPage,
  showOnCompleteHookSettings
}: {
  value: IAutomationActionTemplateCreateDocumentFromTemplate;
  onChange: (
    nextValue: IAutomationActionTemplateCreateDocumentFromTemplate
  ) => void;
  expTpl: TaskBuilderContext;
  aTpl: IAutomationTemplate;
  validation: AutomationBuilderActionValidationStateItem | null;
  resources: IExperienceTemplateResource[];
  onChangeResources: (nextResources: IExperienceTemplateResource[]) => void;
  tokens: TokenDefinition[];
  goToSubPage: (nextSubPage: BlockSettingsSubPage) => void;
  showOnCompleteHookSettings: boolean;
  customFields: CustomUserField[];
  setValidation: React.Dispatch<
    SetStateAction<AutomationBuilderActionValidationState>
  >;
  providerType: CreateDocumentProviderType;
}) => {
  const { t } = useUnsafeTranslation();
  const { org } = useCurrentUser();
  const [checkState, setCheckState] = useState<
    'checking' | 'ok' | 'error' | undefined
  >();

  const [usedVars, setUsedVars] = useState<string[] | undefined>();

  const validateSrcTemplate = useFieldValidator(
    setValidation,
    VALIDATORS_CREATE_DOCUMENT_FROM_TEMPLATE,
    'srcTemplate'
  );

  const validateTarget = useFieldValidator(
    setValidation,
    VALIDATORS_CREATE_DOCUMENT_FROM_TEMPLATE,
    'target'
  );

  const validateFileNameFormat = useFieldValidator(
    setValidation,
    VALIDATORS_CREATE_DOCUMENT_FROM_TEMPLATE,
    'fileNameFormat'
  );

  const getMicrosoftOneDriveFolderResource = (
    res: IExperienceTemplateResource[]
  ) =>
    getUniqueTemplateResource<IExperienceTemplateResourceMicrosoftOneDriveFolder>(
      res,
      TemplateResourceType.MICROSOFT_ONEDRIVE_FOLDER
    );

  const getMicrosoftSharepointFolderResource = (
    res: IExperienceTemplateResource[]
  ) =>
    getUniqueTemplateResource<IExperienceTemplateResourceMicrosoftSharepointFolder>(
      res,
      TemplateResourceType.MICROSOFT_SHAREPOINT_FOLDER
    );

  const invalidSrcTemplate = validation?.fields.srcTemplate || null;
  const invalidTarget = validation?.fields.target || null;

  const interpolationArgs = useMemo(() => {
    return {
      experience: {
        title: expTpl.title,
        adminTitle: expTpl.adminTitle
      },

      participant: userToInterpolation(
        EXAMPLE_USER_MAX('', org.customUserFields),
        org.customUserFields,
        now() // TODO which date to use here?
      ),
      ...mapValues(Object.keys(expTpl.supportingActors), () =>
        EXAMPLE_USER_MAX('', org.customUserFields)
      )
    };
  }, [expTpl, org.customUserFields]);

  const createOneDriveFolderResource = () => {
    let resource = getMicrosoftOneDriveFolderResource(resources);
    if (!resource) {
      resource = {
        type: TemplateResourceType.MICROSOFT_ONEDRIVE_FOLDER,
        resourceId: shortid(),
        name: '',
        publicUrl: '',
        status: 'PENDING',
        folderId: ''
      };
      onChangeResources([...resources, resource]);
    }
    const nextValue: IAutomationActionTemplateCreateDocumentFromTemplate = {
      ...value,
      target: {
        type: SubmitTargetType.ONEDRIVE,
        resourceId: resource.resourceId
      }
    };
    validateTarget(nextValue, expTpl, aTpl);
    onChange(nextValue);
  };

  const createGoogleFolderResource = () => {
    let resource = getGoogleDriveFolderResource(resources);
    if (!resource) {
      resource = {
        type: TemplateResourceType.GOOGLE_DRIVE_FOLDER,
        resourceId: shortid(),
        name: '',
        status: 'PENDING',
        folderId: ''
      };
      onChangeResources([...resources, resource]);
    }
    const nextValue: IAutomationActionTemplateCreateDocumentFromTemplate = {
      ...value,
      target: {
        type: SubmitTargetType.GOOGLE_DRIVE,
        resourceId: resource.resourceId
      }
    };
    validateTarget(nextValue, expTpl, aTpl);
    onChange(nextValue);
  };

  const createSharepointFolderResource = () => {
    let resource = getMicrosoftSharepointFolderResource(resources);
    if (!resource) {
      resource = {
        type: TemplateResourceType.MICROSOFT_SHAREPOINT_FOLDER,
        resourceId: shortid(),
        name: '',
        publicUrl: '',
        status: 'PENDING',
        folderId: '',
        sharepointSiteId: '',
        sharepointSiteName: '',
        driveId: '',
        driveName: ''
      };
      onChangeResources([...resources, resource]);
    }
    const nextValue: IAutomationActionTemplateCreateDocumentFromTemplate = {
      ...value,
      target: {
        type: SubmitTargetType.ONEDRIVE,
        resourceId: resource.resourceId
      }
    };
    validateTarget(nextValue, expTpl, aTpl);
    onChange(nextValue);
  };
  const { dialogOpen, openDialog, closeDialog } = useDialogState();

  useEffect(() => {
    if (
      value.target.type === SubmitTargetType.ONEDRIVE &&
      !value.target.resourceId
    ) {
      createOneDriveFolderResource();
    }

    if (
      value.target.type === SubmitTargetType.SHAREPOINT &&
      !value.target.resourceId
    ) {
      createSharepointFolderResource();
    }

    if (
      value.target.type === SubmitTargetType.GOOGLE_DRIVE &&
      !value.target.resourceId
    ) {
      createGoogleFolderResource();
    }
  }, [value]);

  type CheckDocumentArgs = { orgId: string; docUrl: string };

  useEffect(() => {
    const validateMicrosoftUrlAsync = async () => {
      setCheckState('checking');

      if (value.srcTemplate.type !== FileResourceType.MS_ONE_DRIVE) {
        return;
      }
      const args: CheckDocumentArgs = {
        orgId: org.id,
        docUrl: value.srcTemplate.publicUrl
      };
      const response = await callFirebaseFunction<CheckDocumentResponseMS>(
        CF.microsoft.files.checkDocumentTemplateUrl,
        args
      );

      if (response.status === 'Ok') {
        const nextValue: IAutomationActionTemplateCreateDocumentFromTemplate = {
          ...value,
          srcTemplate: {
            ...value.srcTemplate,
            driveId: response.driveItemData.driveId,
            driveItemId: response.driveItemData.driveItemId
          }
        };
        setCheckState('ok');
        setUsedVars(response.variables);
        validateSrcTemplate(nextValue, expTpl, aTpl);
        onChange(nextValue);
      } else {
        setCheckState('error');
        validateSrcTemplate(value, expTpl, aTpl, response);
      }
    };

    if (
      value.srcTemplate.type === FileResourceType.MS_ONE_DRIVE &&
      value.srcTemplate.publicUrl
    ) {
      if (!isValidUrl(value.srcTemplate.publicUrl)) {
        validateSrcTemplate(value, expTpl, aTpl);
      } else if (!value.srcTemplate.driveId) {
        validateMicrosoftUrlAsync();
      }
    }
  }, [value.srcTemplate]);
  const invalidFileNameTemplateReason =
    validation?.fields.fileNameFormat || null;

  //   if (loading) {
  //     return <Loader />;
  //   }

  //   if (error) {
  //     return <SomethingWentWrong />;
  //   }

  if (!value) {
    return null;
  }

  const actosToShareWith = value.pplToShareWith.filter(
    (p) => p.type === 'actor'
  );

  const othersToShareWith = value.pplToShareWith.filter(
    (p) => p.type === 'other'
  );

  const onCreateGoogleTemplate = async () => {
    const target = value.target;
    if (target.type !== SubmitTargetType.GOOGLE_DRIVE) {
      return;
    }

    const folderResource = resources
      .filter(
        (r): r is IExperienceTemplateResourceGoogleDriveFolder =>
          r.type === TemplateResourceType.GOOGLE_DRIVE_FOLDER
      )
      .find((r) => r.resourceId === target.resourceId);

    const args = {
      orgId: org.id,
      docTitle: `TEMPLATE - ${aTpl.title} `,
      folder:
        folderResource && folderResource.status === 'COMPLETE'
          ? {
              type: 'existing',
              id: folderResource.folderId,
              resource: folderResource.name
            }
          : {
              type: 'create',
              name:
                folderResource?.name ||
                `Dado - ${expTpl.adminTitle || expTpl.title}`
            }
    };

    const response = await callFirebaseFunction<{
      docId: string;
      docTitle: string;
      folderId: string;
      folderName: string;
    }>(CF.google.documents.createEmptyDocument, args);

    const newDocResource: IExperienceTemplateResourceGoogleDoc = {
      type: TemplateResourceType.GOOGLE_DOC_TEMPLATE,
      docId: response.docId,
      docTitle: response.docTitle,
      resourceId: shortid(),
      status: 'COMPLETE',
      docStatus: 'COMPLETE'
    };

    const newResources: IExperienceTemplateResource[] = [newDocResource];

    let folderResourceId = '';
    if (folderResource) {
      folderResourceId = folderResource.resourceId;
      folderResource.name = response.folderName;
      folderResource.folderId = response.folderId;
      folderResource.status = 'COMPLETE';
    } else {
      const newFolderResource: IExperienceTemplateResourceGoogleDriveFolder = {
        type: TemplateResourceType.GOOGLE_DRIVE_FOLDER,
        folderId: response.folderId,
        name: response.folderName,
        resourceId: shortid(),
        status: 'COMPLETE'
      };
      newResources.push(newFolderResource);
      folderResourceId = newFolderResource.folderId;
    }

    const nextResources = [...resources, ...newResources];

    onChangeResources(nextResources);

    onChange({
      ...value,
      srcTemplate: {
        type: FileResourceType.GOOGLE_DRIVE,
        resourceId: newDocResource.resourceId
      },
      target: {
        type: SubmitTargetType.GOOGLE_DRIVE,
        resourceId: folderResourceId
      }
    });
  };

  console.log('XXX', providerType, value);

  if (providerType === CreateDocumentProviderType.GOOGLE) {
    return (
      <FlexContainerVertical spacing={3} fullWidth>
        <FlexContainerVertical spacing={2} fullWidth>
          <Typography variant="h5" gutterBottom>
            {t('Folder for created documents')}
          </Typography>

          <SendToSelector
            value={value.target.type}
            allowedTypes={[SubmitTargetType.GOOGLE_DRIVE]}
            onChange={(nextType) => {
              if (nextType === SubmitTargetType.GOOGLE_DRIVE) {
                let resource = getGoogleDriveFolderResource(resources);
                if (!resource) {
                  resource = {
                    type: TemplateResourceType.GOOGLE_DRIVE_FOLDER,
                    resourceId: shortid(),
                    name: '',
                    status: 'PENDING',
                    folderId: ''
                  };
                  onChangeResources([...resources, resource]);
                }
                const nextValue: IAutomationActionTemplateCreateDocumentFromTemplate = {
                  ...value,
                  target: {
                    type: nextType,
                    resourceId: resource.resourceId
                  }
                };
                validateTarget(nextValue, expTpl, aTpl);
                onChange(nextValue);
                return;
              }
            }}
          />

          {value.target.type === SubmitTargetType.GOOGLE_DRIVE && (
            <TextField
              label={`${t('Folder name')}`}
              value={getGoogleDriveFolderResource(resources)?.name || ''}
              fullWidth
              helperText={`${t(
                'Dado will create this folder. Don’t choose the name of an existing folder in this drive.'
              )}`}
              disabled={
                getGoogleDriveFolderResource(resources)?.status === 'COMPLETE'
              }
              variant="outlined"
              onChange={(ev) => {
                const resourcesCopy = [...resources];
                const resource = getGoogleDriveFolderResource(resourcesCopy);
                if (!resource) {
                  // Should have been created earlier
                  return;
                }
                if (value.target.type === SubmitTargetType.GOOGLE_DRIVE) {
                  const nextValue: IAutomationActionTemplateCreateDocumentFromTemplate = {
                    ...value,
                    target: {
                      ...value.target,
                      resourceId: resource.resourceId
                    }
                  };

                  const nextResources = resources.map((r) => {
                    if (r.resourceId !== resource.resourceId) {
                      return r;
                    }
                    return {
                      ...r,
                      name: ev.target.value
                    };
                  });
                  onChangeResources(nextResources);
                  validateTarget(nextValue, expTpl, aTpl);
                  onChange(nextValue);
                }
              }}
            />
          )}
        </FlexContainerVertical>

        <FlexContainerVertical spacing={2} fullWidth>
          <Typography variant="h5" gutterBottom>
            {t('Template')}
          </Typography>

          {providerType === CreateDocumentProviderType.GOOGLE &&
            value.srcTemplate.type === FileResourceType.GOOGLE_DRIVE &&
            !value.srcTemplate.resourceId && (
              <FlexContainerVertical spacing={1} fullWidth>
                <ButtonWithPromise
                  color="primary"
                  onClick={onCreateGoogleTemplate}
                  pending={t('Creating...')}
                  size="small"
                  startIcon={<PlusCircle size={16} />}
                >
                  {t('Create template document')}
                </ButtonWithPromise>
                <FormHelperText>
                  {t(
                    'Make this document into a template version  of the documents you’d like Dado to create.'
                  )}
                </FormHelperText>
              </FlexContainerVertical>
            )}

          {providerType === CreateDocumentProviderType.GOOGLE &&
            value.srcTemplate.type === FileResourceType.GOOGLE_DRIVE &&
            value.srcTemplate.resourceId && (
              <FlexContainerVertical spacing={1} fullWidth>
                <Button
                  size="small"
                  color="primary"
                  endIcon={<ExternalLink size={14} />}
                  target="_blank"
                  href={toGoogleDocLink(
                    resources.find(
                      (r): r is IExperienceTemplateResourceGoogleDoc =>
                        value.srcTemplate.type ===
                          FileResourceType.GOOGLE_DRIVE &&
                        value.srcTemplate.resourceId === r.resourceId
                    )?.docId || ''
                  )}
                >
                  {t('Template document')}
                </Button>
                <FormHelperText>
                  <div>
                    {t(
                      `Make this document into a template version of the documents you'd like Dado to create.`
                    )}
                  </div>
                  <div>
                    {t(
                      'If you amend this document, the updated version will be used  the next time the automation runs.'
                    )}
                  </div>
                </FormHelperText>
                <Button
                  size="small"
                  onClick={() => openDialog()}
                  color="primary"
                  endIcon={<ExternalLink size={14} />}
                >
                  {t('Use these tokens to insert Dado data')}
                </Button>
                <TokenSyntaxHelpDialog
                  open={dialogOpen}
                  onClose={closeDialog}
                  tokens={tokens}
                />
              </FlexContainerVertical>
            )}
        </FlexContainerVertical>

        <FlexContainerVertical spacing={2} fullWidth>
          <Typography variant="h5" gutterBottom>
            {t('Created document details')}
          </Typography>
          <TextFieldWithReplacementTokens
            label={t('File name')}
            value={value.fileNameFormat}
            fullWidth
            variant="outlined"
            error={!!invalidFileNameTemplateReason}
            helperText={
              invalidFileNameTemplateReason ||
              `${t('Example')}: ${interpolate(
                value.fileNameFormat,
                interpolationArgs
              )}`
            }
            onBlur={() => validateFileNameFormat(value, expTpl, aTpl)}
            onChange={(fileNameFormat) => {
              const nextValue = {
                ...value,
                fileNameFormat: fileNameFormat
              };

              validateFileNameFormat(nextValue, expTpl, aTpl);
              onChange(nextValue);
            }}
            tokens={tokens}
          />

          <ActionActorSelectorMulti
            label={t('Grant access to')}
            value={actosToShareWith}
            onChange={(nextActorsToShareWith) => {
              const nextValue: IAutomationActionTemplateCreateDocumentFromTemplate = {
                ...value,
                pplToShareWith: [...nextActorsToShareWith, ...othersToShareWith]
              };

              return onChange(nextValue);
            }}
            expTpl={expTpl}
            fullWidth
            filterOptions={(options) => {
              return options
                .filter((o) => o.type === 'actor')
                .filter(
                  (option) =>
                    option.type === 'actor' &&
                    !actosToShareWith.find(
                      (a) =>
                        a.type === 'actor' &&
                        a.emailField === option.emailField &&
                        a.key === option.key
                    )
                );
            }}
          />

          <AutocompleteMultiFreeSolo
            label={t('Additional access granted to (email address)')}
            fullWidth
            value={compact(
              othersToShareWith.map((p) =>
                p.type === 'other' ? p.email : null
              )
            )}
            onChange={(nextEmails) => {
              const emailToshareWith: ActionActor[] = nextEmails.map(
                (email) => ({
                  type: 'other',
                  email,
                  optional: false
                })
              );

              const nextValue: IAutomationActionTemplateCreateDocumentFromTemplate = {
                ...value,
                pplToShareWith: [...actosToShareWith, ...emailToshareWith]
              };

              return onChange(nextValue);
            }}
            options={() =>
              getUsersByOrgId(expTpl.orgId).then((us) =>
                us.map((u) => u.data.email).filter((t) => t)
              )
            }
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  variant="default"
                  size="small"
                  label={option}
                  {...getTagProps({ index })}
                />
              ))
            }
          />
        </FlexContainerVertical>

        <OnCompleteHooks
          value={value.hooks.onComplete}
          tokens={[
            ...tokens,
            {
              label: 'Created Document Url',
              group: 'Action',
              token: 'action.createdDocPublicUrl',
              color: 'green',
              type: 'string'
            }
          ]}
          onChange={(nextHooks) => {
            const nextValues = {
              ...value,
              hooks: {
                ...value.hooks,
                onComplete: nextHooks
              }
            };
            //validateHooks(nextValues, expTpl);
            onChange(nextValues);
          }}
          resources={resources}
          onChangeResources={onChangeResources}
          expTpl={expTpl}
          customFields={customFields}
          goToSubPage={goToSubPage}
          validation={validation}
          showOnCompleteHookSettings={showOnCompleteHookSettings}
          storageContext={StorageContext.AUTOMATON_ACTION}
          defaultCompletionText="{{ action.createdDocPublicUrl }}"
        />
      </FlexContainerVertical>
    );
  }

  return (
    <FlexContainerVertical spacing={3} fullWidth>
      <FlexContainerVertical spacing={2} fullWidth>
        <Typography variant="h5" gutterBottom>
          {t('Template')}
        </Typography>

        {providerType === CreateDocumentProviderType.MICROSOFT &&
          value.srcTemplate.type === FileResourceType.MS_ONE_DRIVE && (
            <TextField
              fullWidth
              placeholder={t('Template URL')}
              error={!!invalidSrcTemplate}
              helperText={invalidSrcTemplate || ''}
              value={value.srcTemplate.publicUrl}
              InputProps={{
                endAdornment:
                  checkState === 'checking' ? (
                    <InlineLoader size={16} />
                  ) : checkState === 'ok' ? (
                    <CheckCircle color="green" size={16} />
                  ) : undefined
              }}
              onChange={(ev) => {
                const nextUrl = ev.target.value;

                const srcTemplate: IAutomationActionTemplateCreateDocumentFromTemplate['srcTemplate'] = {
                  type: FileResourceType.MS_ONE_DRIVE,
                  publicUrl: nextUrl
                };

                const nextValue: IAutomationActionTemplateCreateDocumentFromTemplate = {
                  ...value,
                  srcTemplate
                };
                setCheckState(undefined);

                validateSrcTemplate(nextValue, expTpl, aTpl);
                onChange(nextValue);
              }}
            />
          )}

        {checkState === 'ok' && usedVars && usedVars.length === 0 && (
          <InfoBox>
            {t(
              `The provided template document does not contain any Dado variables. Please make sure you provided the right document.`
            )}
          </InfoBox>
        )}

        <Button
          size="small"
          onClick={() => openDialog()}
          color="primary"
          startIcon={<ExternalLink size={14} />}
        >
          {t('Variables to insert Dado data')}
        </Button>
        <TokenSyntaxHelpDialog
          open={dialogOpen}
          onClose={closeDialog}
          tokens={tokens}
        />
      </FlexContainerVertical>
      <FlexContainerVertical spacing={2} fullWidth>
        <Typography variant="h5" gutterBottom>
          {t('Created documents')}
        </Typography>

        <SendToSelector
          value={value.target.type}
          allowedTypes={[
            SubmitTargetType.ONEDRIVE,
            SubmitTargetType.SHAREPOINT
          ]}
          onChange={(nextType) => {
            if (nextType === SubmitTargetType.ONEDRIVE) {
              let resource = getMicrosoftOneDriveFolderResource(resources);
              if (!resource) {
                resource = {
                  type: TemplateResourceType.MICROSOFT_ONEDRIVE_FOLDER,
                  resourceId: shortid(),
                  name: '',
                  publicUrl: '',
                  status: 'PENDING',
                  folderId: ''
                };
                onChangeResources([...resources, resource]);
              }
              const nextValue: IAutomationActionTemplateCreateDocumentFromTemplate = {
                ...value,
                target: {
                  type: nextType,
                  resourceId: resource.resourceId
                }
              };
              validateTarget(nextValue, expTpl, aTpl);
              onChange(nextValue);
              return;
            }

            if (nextType === SubmitTargetType.SHAREPOINT) {
              let resource = getMicrosoftSharepointFolderResource(resources);
              if (!resource) {
                resource = {
                  type: TemplateResourceType.MICROSOFT_SHAREPOINT_FOLDER,
                  resourceId: shortid(),
                  name: '',
                  publicUrl: '',
                  status: 'PENDING',
                  folderId: '',
                  sharepointSiteId: '',
                  sharepointSiteName: '',
                  driveId: '',
                  driveName: ''
                };
                onChangeResources([...resources, resource]);
              }
              const nextValue: IAutomationActionTemplateCreateDocumentFromTemplate = {
                ...value,
                target: {
                  type: nextType,
                  resourceId: resource.resourceId
                }
              };
              validateTarget(nextValue, expTpl, aTpl);
              onChange(nextValue);
              return;
            }
          }}
        />

        {value.target.type === SubmitTargetType.GOOGLE_DRIVE && (
          <TextField
            label={`${t('Folder name')}`}
            value={getGoogleDriveFolderResource(resources)?.name || ''}
            fullWidth
            helperText={`${t(
              'Dado will create this folder the first time this automation runs. Be careful not to choose the name of an existing folder in this drive'
            )}`}
            variant="outlined"
            onChange={(ev) => {
              const resourcesCopy = [...resources];
              const resource = getGoogleDriveFolderResource(resourcesCopy);
              if (!resource) {
                createGoogleFolderResource();
                return;
              }
              if (value.target.type === SubmitTargetType.GOOGLE_DRIVE) {
                const nextValue: IAutomationActionTemplateCreateDocumentFromTemplate = {
                  ...value,
                  target: {
                    ...value.target,
                    resourceId: resource.resourceId
                  }
                };

                const nextResources = resources.map((r) => {
                  if (r.resourceId !== resource.resourceId) {
                    return r;
                  }
                  return {
                    ...r,
                    name: ev.target.value
                  };
                });
                onChangeResources(nextResources);
                validateTarget(nextValue, expTpl, aTpl);
                onChange(nextValue);
              }
            }}
          />
        )}

        {value.target.type === SubmitTargetType.ONEDRIVE && (
          <TextField
            label={`${t('Folder name')}`}
            value={getMicrosoftOneDriveFolderResource(resources)?.name || ''}
            fullWidth
            error={!!invalidTarget}
            helperText={
              invalidTarget ||
              `${t(
                'Dado will create this folder the first time this automation runs. Be careful not to choose the name of an existing folder in this drive'
              )}`
            }
            variant="outlined"
            onChange={(ev) => {
              const resourcesCopy = [...resources];
              let resource = getMicrosoftOneDriveFolderResource(resourcesCopy);
              if (!resource) {
                createOneDriveFolderResource(); //should not happen
              }
              if (
                value.target.type === SubmitTargetType.ONEDRIVE ||
                value.target.type === SubmitTargetType.SHAREPOINT
              ) {
                const nextValue: IAutomationActionTemplateCreateDocumentFromTemplate = {
                  ...value,
                  target: {
                    ...value.target,
                    resourceId: resource.resourceId
                  }
                };

                const nextResources = resources.map((r) => {
                  if (r.resourceId !== resource.resourceId) {
                    return r;
                  }
                  return {
                    ...r,
                    name: ev.target.value
                  };
                });
                onChangeResources(nextResources);
                validateTarget(nextValue, expTpl, aTpl);
                onChange(nextValue);
              }
            }}
          />
        )}

        {value.target.type === SubmitTargetType.SHAREPOINT && (
          <SharepointSiteAndDriveSelector
            orgId={org.id}
            value={getMicrosoftSharepointFolderResource(resources)}
            onChange={(nextSharpointsite) => {
              const resource = getMicrosoftSharepointFolderResource(resources);
              if (!resource) {
                // can never happen
                return;
              }

              const nextResources = resources.map((r) => {
                if (
                  r.resourceId !== resource.resourceId ||
                  resource.type !==
                    TemplateResourceType.MICROSOFT_SHAREPOINT_FOLDER
                ) {
                  return r;
                }
                const updatedResource = {
                  ...r,
                  sharepointSiteId: nextSharpointsite.sharepointSiteId,
                  sharepointSiteName: nextSharpointsite.sharepointSiteName,
                  driveName: nextSharpointsite.driveName,
                  driveId: nextSharpointsite.driveId
                } as IExperienceTemplateResourceMicrosoftSharepointFolder;

                return updatedResource;
              });
              onChangeResources(nextResources);
            }}
          />
        )}

        {value.target.type === SubmitTargetType.SHAREPOINT && (
          <TextField
            label={`${t('Folder name')}`}
            value={getMicrosoftSharepointFolderResource(resources)?.name || ''}
            fullWidth
            helperText={`${t(
              'You cannot use the name of an existing folder in this drive'
            )}`}
            variant="outlined"
            onChange={(ev) => {
              const resource = getMicrosoftSharepointFolderResource(resources);
              if (!resource) {
                // can never happen
                return;
              }
              const nextResources = resources.map((r) => {
                if (r.resourceId !== resource.resourceId) {
                  return r;
                }
                return {
                  ...r,
                  name: ev.target.value
                };
              });
              onChangeResources(nextResources);

              validateTarget(value, expTpl, aTpl);
            }}
          />
        )}

        <TextFieldWithReplacementTokens
          label={t('Document name')}
          value={value.fileNameFormat}
          fullWidth
          variant="outlined"
          error={!!invalidFileNameTemplateReason}
          helperText={
            invalidFileNameTemplateReason ||
            `${t('Example')}: ${interpolate(
              value.fileNameFormat,
              interpolationArgs
            )}`
          }
          onBlur={() => validateFileNameFormat(value, expTpl, aTpl)}
          onChange={(fileNameFormat) => {
            const nextValue = {
              ...value,
              fileNameFormat: fileNameFormat
            };

            validateFileNameFormat(nextValue, expTpl, aTpl);
            onChange(nextValue);
          }}
          tokens={tokens}
        />

        <ActionActorSelectorMulti
          label={t('Grant access to')}
          value={actosToShareWith}
          onChange={(nextActorsToShareWith) => {
            const nextValue: IAutomationActionTemplateCreateDocumentFromTemplate = {
              ...value,
              pplToShareWith: [...nextActorsToShareWith, ...othersToShareWith]
            };

            return onChange(nextValue);
          }}
          expTpl={expTpl}
          fullWidth
          filterOptions={(options) => {
            return options
              .filter((o) => o.type === 'actor')
              .filter(
                (option) =>
                  option.type === 'actor' &&
                  !actosToShareWith.find(
                    (a) =>
                      a.type === 'actor' &&
                      a.emailField === option.emailField &&
                      a.key === option.key
                  )
              );
          }}
        />

        <AutocompleteMultiFreeSolo
          label={t('Additional access granted to (email address)')}
          fullWidth
          value={compact(
            othersToShareWith.map((p) => (p.type === 'other' ? p.email : null))
          )}
          onChange={(nextEmails) => {
            const emailToshareWith: ActionActor[] = nextEmails.map((email) => ({
              type: 'other',
              email,
              optional: false
            }));

            const nextValue: IAutomationActionTemplateCreateDocumentFromTemplate = {
              ...value,
              pplToShareWith: [...actosToShareWith, ...emailToshareWith]
            };

            return onChange(nextValue);
          }}
          options={() =>
            getUsersByOrgId(expTpl.orgId).then((us) =>
              us.map((u) => u.data.email).filter((t) => t)
            )
          }
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip
                variant="default"
                size="small"
                label={option}
                {...getTagProps({ index })}
              />
            ))
          }
        />
      </FlexContainerVertical>

      <OnCompleteHooks
        value={value.hooks.onComplete}
        tokens={[
          ...tokens,
          {
            label: 'Created Document Url',
            group: 'Action',
            token: 'action.createdDocPublicUrl',
            color: 'green',
            type: 'string'
          }
        ]}
        onChange={(nextHooks) => {
          const nextValues = {
            ...value,
            hooks: {
              ...value.hooks,
              onComplete: nextHooks
            }
          };
          //validateHooks(nextValues, expTpl);
          onChange(nextValues);
        }}
        resources={resources}
        onChangeResources={onChangeResources}
        expTpl={expTpl}
        customFields={customFields}
        goToSubPage={goToSubPage}
        validation={validation}
        showOnCompleteHookSettings={showOnCompleteHookSettings}
        storageContext={StorageContext.AUTOMATON_ACTION}
        defaultCompletionText="{{ action.createdDocPublicUrl }}"
      />
    </FlexContainerVertical>
  );
};

export const ActionSettingsCreateDocumentFromTemplateReadOnly = ({
  value,
  expTpl,
  resources
}: {
  value: IAutomationActionTemplateCreateDocumentFromTemplate;
  expTpl: TaskBuilderContext;
  resources: IExperienceTemplateResource[];
}) => {
  const { t } = useUnsafeTranslation();

  const resource = resources.find((r) => {
    if (value.target.type === 'EMAIL') {
      return false;
    }

    return r.resourceId === value.target.resourceId;
  });

  const getPublicUrl = (
    aTpl: IAutomationActionTemplateCreateDocumentFromTemplate
  ) => {
    const srcTpl = aTpl.srcTemplate;
    // AI-generated actions can arrive with a null/missing srcTemplate. This
    // runs during the summary render, so guard before dereferencing `.type`
    // to keep a malformed action from taking down the whole Automation tab.
    if (!srcTpl) return '';
    if (srcTpl.type === FileResourceType.MS_ONE_DRIVE) {
      return srcTpl.publicUrl;
    }
    const resource = resources
      .filter(
        (r): r is IExperienceTemplateResourceGoogleDoc =>
          r.type === TemplateResourceType.GOOGLE_DOC_TEMPLATE
      )
      .find((r) => r.resourceId === srcTpl.resourceId);

    if (!resource) {
      return '';
    }

    return toGoogleDocLink(resource.docId);
  };

  return (
    <div>
      <FormRow>
        <DataFieldWithLabel
          preventOverflow={true}
          label={`${t('Template Document')}`}
        >
          <Link rel="noopener" target="_blank" href={getPublicUrl(value)}>
            <Typography noWrap>{getPublicUrl(value)}</Typography>
          </Link>
        </DataFieldWithLabel>
      </FormRow>

      <FormRow>
        <DataFieldWithLabel label={`${t('Created documents')}`}>
          {resource && <Resource d={resource} />}
        </DataFieldWithLabel>
      </FormRow>

      <FormRow>
        <DataFieldWithLabel label={t('Name format for created docs')}>
          {value.fileNameFormat}
        </DataFieldWithLabel>
      </FormRow>
    </div>
  );
};
